'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Users, Bed, Bath, Star, ArrowRight, Wifi, Wind } from 'lucide-react';
import { type Apartment } from '@/lib/supabase/apartments';

interface ApartmentCardProps {
  apartment: Apartment;
  viewMode: 'grid' | 'list';
}

function formatTZS(amount: number): string {
  return `TZS ${Number(amount).toLocaleString()}`;
}

function getCardImage(apt: Apartment): string {
  if (apt.apartment_images && apt.apartment_images.length > 0) {
    const primary = apt.apartment_images.find((i) => i.is_primary);
    return primary?.url || apt.apartment_images[0].url;
  }
  return apt.cover_image_url || '/assets/images/no_image.png';
}

export default function ApartmentCard({ apartment: apt, viewMode }: ApartmentCardProps) {
  const imgSrc = getCardImage(apt);

  if (viewMode === 'list') {
    return (
      <div
        className="bg-white overflow-hidden hover:shadow-[0_8px_32px_rgba(11,22,40,0.10)] transition-all duration-300 flex flex-col sm:flex-row group"
        style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
      >
        {/* Image */}
        <div className="relative w-full sm:w-72 h-52 sm:h-auto shrink-0 overflow-hidden img-zoom-hover">
          <AppImage
            src={imgSrc}
            alt={apt.name}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, 288px"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(5,13,26,0.6) 0%, transparent 60%)' }}
          />
          {apt.tag && (
            <span
              className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold tracking-widest uppercase"
              style={{ background: '#C9A96E', color: '#050D1A', borderRadius: '2px' }}
            >
              {apt.tag}
            </span>
          )}
          <span
            className="absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold tracking-wide uppercase"
            style={{
              background: apt.is_available ? 'rgba(45,106,79,0.88)' : 'rgba(11,22,40,0.80)',
              color: 'white',
              borderRadius: '2px',
              backdropFilter: 'blur(4px)',
            }}
          >
            {apt.is_available ? 'Available' : 'Booked'}
          </span>
          {/* Rating */}
          <div
            className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1"
            style={{ background: 'rgba(5,13,26,0.65)', backdropFilter: 'blur(4px)', borderRadius: '2px' }}
          >
            <Star size={10} className="text-[#C9A96E] fill-[#C9A96E]" />
            <span className="text-white text-xs font-semibold">5.0</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 flex flex-col justify-between">
          <div>
            <h3
              className="font-semibold text-[#0B1628] leading-tight mb-1.5 tracking-wide"
              style={{ fontSize: '1rem', letterSpacing: '0.03em' }}
            >
              {apt.name}
            </h3>
            <div className="flex items-center gap-1 text-[#6B6560] text-sm mb-3">
              <MapPin size={12} className="text-[#C9A96E]" />
              <span>{apt.location}</span>
            </div>
            <p className="text-sm text-[#6B6560] mb-4 line-clamp-2 leading-relaxed">{apt.description}</p>
            <div className="flex items-center gap-4 text-sm text-[#6B6560] mb-3 flex-wrap">
              <div className="flex items-center gap-1"><Users size={13} className="text-[#C9A96E]" /><span>{apt.capacity} guests</span></div>
              <div className="flex items-center gap-1"><Bed size={13} className="text-[#C9A96E]" /><span>{apt.bedrooms} bed{apt.bedrooms > 1 ? 's' : ''}</span></div>
              <div className="flex items-center gap-1"><Bath size={13} className="text-[#C9A96E]" /><span>{apt.bathrooms} bath{apt.bathrooms > 1 ? 's' : ''}</span></div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(apt.amenities || []).slice(0, 4).map((am) => (
                <span
                  key={am}
                  className="px-2.5 py-1 text-xs font-medium text-[#6B6560] tracking-wide"
                  style={{ background: '#F0EDE8', borderRadius: '2px' }}
                >
                  {am}
                </span>
              ))}
              {(apt.amenities || []).length > 4 && (
                <span className="text-xs text-[#6B6560]">+{apt.amenities.length - 4} more</span>
              )}
            </div>
          </div>
          <div
            className="flex items-end justify-between mt-5 pt-4"
            style={{ borderTop: '1px solid #F0EDE8' }}
          >
            <div>
              <p
                className="text-xl font-bold text-[#0B1628] tabular-nums"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400 }}
              >
                {formatTZS(Number(apt.price_per_night))}
              </p>
              <p className="text-xs text-[#6B6560]">per night</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                href={`/apartments/${apt.id}`}
                className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold tracking-widest uppercase btn-scale transition-all hover:bg-[#F0EDE8]"
                style={{ border: '1px solid #E2DDD6', color: '#0B1628', borderRadius: '2px', letterSpacing: '0.08em' }}
              >
                View
              </Link>
              <Link
                href={`/apartments/${apt.id}`}
                className={`flex items-center gap-2 px-5 py-2.5 text-xs font-semibold tracking-widest uppercase btn-scale transition-all ${
                  apt.is_available ? 'hover:brightness-110' : 'opacity-50 pointer-events-none'
                }`}
                style={{
                  background: apt.is_available ? '#C9A96E' : '#E2DDD6',
                  color: apt.is_available ? '#050D1A' : '#6B6560',
                  borderRadius: '2px',
                  letterSpacing: '0.08em',
                }}
              >
                {apt.is_available ? 'Book Now' : 'Unavailable'}
                {apt.is_available && <ArrowRight size={13} />}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view — luxury hotel card
  return (
    <div
      className="bg-white overflow-hidden card-hover group flex flex-col"
      style={{
        border: '1px solid #E2DDD6',
        borderRadius: '2px',
        boxShadow: '0 2px 12px rgba(11,22,40,0.05)',
      }}
    >
      {/* Image with zoom */}
      <div className="relative h-60 overflow-hidden img-zoom-hover">
        <AppImage
          src={imgSrc}
          alt={apt.name}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-107"
          sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
        />
        {/* Gradient */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(5,13,26,0.80) 0%, rgba(5,13,26,0.15) 50%, transparent 100%)' }}
        />

        {/* Tag */}
        {apt.tag && (
          <span
            className="absolute top-3 left-3 px-3 py-1 text-xs font-semibold tracking-widest uppercase"
            style={{ background: '#C9A96E', color: '#050D1A', borderRadius: '2px' }}
          >
            {apt.tag}
          </span>
        )}

        {/* Availability */}
        <span
          className="absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold tracking-wide uppercase"
          style={{
            background: apt.is_available ? 'rgba(45,106,79,0.88)' : 'rgba(11,22,40,0.80)',
            color: 'white',
            borderRadius: '2px',
            backdropFilter: 'blur(4px)',
          }}
        >
          {apt.is_available ? 'Available' : 'Booked'}
        </span>

        {/* Rating */}
        <div
          className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1"
          style={{
            background: 'rgba(5,13,26,0.65)',
            backdropFilter: 'blur(6px)',
            borderRadius: '2px',
            border: '1px solid rgba(201,169,110,0.2)',
          }}
        >
          <Star size={10} className="text-[#C9A96E] fill-[#C9A96E]" />
          <span className="text-white text-xs font-semibold">5.0</span>
        </div>

        {/* Price on image */}
        <div className="absolute bottom-3 right-3 text-right">
          <p
            className="text-white font-bold tabular-nums"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: '0.95rem',
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            }}
          >
            {formatTZS(Number(apt.price_per_night))}
          </p>
          <p className="text-white/60" style={{ fontSize: '0.58rem', letterSpacing: '0.1em' }}>/ NIGHT</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3
          className="font-semibold text-[#0B1628] leading-tight mb-1.5"
          style={{ fontSize: '0.92rem', letterSpacing: '0.03em' }}
        >
          {apt.name}
        </h3>
        <div className="flex items-center gap-1 text-[#6B6560] text-xs mb-3">
          <MapPin size={10} className="text-[#C9A96E] shrink-0" />
          <span className="truncate">{apt.location}</span>
        </div>

        {/* Amenity pills */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <div
            className="flex items-center gap-1 px-2 py-1"
            style={{ background: '#F0EDE8', borderRadius: '2px' }}
          >
            <Wifi size={10} className="text-[#C9A96E]" />
            <span className="text-xs font-medium text-[#6B6560]">WiFi</span>
          </div>
          <div
            className="flex items-center gap-1 px-2 py-1"
            style={{ background: '#F0EDE8', borderRadius: '2px' }}
          >
            <Wind size={10} className="text-[#C9A96E]" />
            <span className="text-xs font-medium text-[#6B6560]">AC</span>
          </div>
          {(apt.amenities || []).length > 2 && (
            <span className="text-xs text-[#6B6560]">+{apt.amenities.length - 2}</span>
          )}
        </div>

        {/* Features */}
        <div
          className="flex items-center gap-4 text-xs text-[#6B6560] mb-5 py-3"
          style={{ borderTop: '1px solid #F0EDE8', borderBottom: '1px solid #F0EDE8' }}
        >
          <div className="flex items-center gap-1"><Users size={11} className="text-[#C9A96E]" /><span>{apt.capacity}</span></div>
          <div className="flex items-center gap-1"><Bed size={11} className="text-[#C9A96E]" /><span>{apt.bedrooms} bed</span></div>
          <div className="flex items-center gap-1"><Bath size={11} className="text-[#C9A96E]" /><span>{apt.bathrooms} bath</span></div>
        </div>

        {/* CTA buttons */}
        <div className="flex items-center gap-2 mt-auto">
          <Link
            href={`/apartments/${apt.id}`}
            className="flex-1 flex items-center justify-center px-3 py-2.5 text-xs font-semibold tracking-widest uppercase transition-all btn-scale hover:bg-[#F0EDE8]"
            style={{ border: '1px solid #E2DDD6', color: '#0B1628', borderRadius: '2px', letterSpacing: '0.08em' }}
          >
            View
          </Link>
          <Link
            href={`/apartments/${apt.id}`}
            className={`flex-1 flex items-center justify-center px-3 py-2.5 text-xs font-semibold tracking-widest uppercase transition-all btn-scale ${
              apt.is_available ? 'hover:brightness-110' : 'opacity-50 pointer-events-none'
            }`}
            style={{
              background: apt.is_available ? '#C9A96E' : '#E2DDD6',
              color: apt.is_available ? '#050D1A' : '#6B6560',
              borderRadius: '2px',
              letterSpacing: '0.08em',
            }}
          >
            {apt.is_available ? 'Book Now' : 'Unavailable'}
          </Link>
        </div>
      </div>
    </div>
  );
}